import { useCallback, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { identity, noop } from "lodash"
import useResizeObserver from "hooks/resize-observer"
import MultiLine from "components/charts/multi-line"
import AnalyzeTooltip from "components/analyze-tooltip"
import { smallPercentageFormatter } from "helpers/formatters/numbers"
import { useMultiLineHandlers } from "./hooks/use-multiline-handlers"
import { useSeriesSelection } from "./hooks/use-series-selection"
import multiDateContract from "gatherers/multi-date/contract"
import { GraphLoadingState, GraphEmptyState } from "./components/graph-states"
import GraphErrorFallback from "./components/graph-error-fallback"
import ChartNotice from "./components/chart-notice"
import ScrollableLegend from "./components/scrollable-legend"
import LegendItem from "components/charts/multi-line/legend-item"
import LegendPanel from "./components/legend-panel"
import { getLatestItemValues, getLatestItemDates, getLatestItemBatchIds } from "./helpers"
import { LOADING, ERROR, OK } from "helpers/data-statuses"
import { MULTI_LINE_ITEM_SHAPE, ADD_SERIES_METRICS_SHAPE, SERIES_SELECTION_SHAPE } from "./prop-types"
import { THRESHOLD_PARAMS } from "helpers/threshold"
import { ErrorBoundary } from "@sentry/react"

import "./styles.scss"

const EMPTY_ARRAY = []
const EMPTY_LATEST_VALUES = {}
const EMPTY_LATEST_DATES = {}
const EMPTY_LATEST_BATCH_IDS = {}

function MultiLineGraphWithError({ status, errorMessage, ...props }) {
  // Leverage ErrorBoundary to catch errors and display a fallback component
  if (status === ERROR) {
    throw new Error(errorMessage)
  }

  return <MultiLineGraphComponent {...props} />
}

MultiLineGraphWithError.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, OK]),
  errorMessage: PropTypes.string,
}

function MultiLineGraphComponent({
  data,
  endDate,
  startDate,
  items = EMPTY_ARRAY,
  filterItems = identity,
  setItems = noop,
  setOnlyItem = noop,
  enableItems = noop,
  emptyMessage,
  noDataMessage,
  formatter = smallPercentageFormatter,
  thresholdParams,
  onDotClick,
  // Reuses the template's own buildDrilldownIntent call, seeded with the item's own last date/batchId.
  onLegendValueClick,
  // Owned by the parent template; recomputing here would drop its pinned-batch exclusion.
  isOneDayMode = false,
  intradayChartMode = "none",
  isTruncated = false,
  hasFailedBatches = false,
  // Defaults to inline (unchanged); a template opts into the right-hand panel explicitly.
  legendPlacement = "inline",
  // Right-hand panel only; omitted by a template with no resolvable metric family (Beta).
  addSeriesMetrics,
  alreadyShownMetricIds,
  onAddSeries,
  seriesSelection,
  // The raw addedMetrics ids, so a no-data add can be told apart from a built-in with no data.
  addedMetricIds = EMPTY_ARRAY,
}) {
  const filteredItems = useMemo(() => filterItems(items, data), [filterItems, items, data])

  // An added factor with no data for the period renders nowhere, so surface it or the pick looks like it did nothing.
  const noDataAddedCount = useMemo(() => {
    if (!addedMetricIds.length) return 0
    const filteredIds = new Set(filteredItems.map(item => item.id))
    return addedMetricIds.filter(id => !filteredIds.has(id)).length
  }, [addedMetricIds, filteredItems])

  // Right-hand legend panel only — the inline top legend doesn't show a value per row.
  const latestValues = useMemo(
    () => (legendPlacement === "right" ? getLatestItemValues(data, filteredItems) : EMPTY_LATEST_VALUES),
    [legendPlacement, data, filteredItems],
  )
  const latestDates = useMemo(
    () => (legendPlacement === "right" ? getLatestItemDates(data, filteredItems) : EMPTY_LATEST_DATES),
    [legendPlacement, data, filteredItems],
  )
  const latestBatchIds = useMemo(
    () => (legendPlacement === "right" ? getLatestItemBatchIds(data, filteredItems) : EMPTY_LATEST_BATCH_IDS),
    [legendPlacement, data, filteredItems],
  )
  const visibleCount = useMemo(
    () => filteredItems.filter(item => !item.disabled).length,
    [filteredItems],
  )

  const handleLegendValueClick = useCallback(
    itemId => onLegendValueClick?.(itemId, latestDates[itemId], latestBatchIds[itemId]),
    [onLegendValueClick, latestDates, latestBatchIds],
  )

  const graphRef = useRef(null)
  const [width, height] = useResizeObserver(graphRef)

  const numYTicks = useMemo(() => {
    return Math.min(Math.max(Math.floor(height / 55), 3), 6)
  }, [height])

  const selection = useSeriesSelection(filteredItems, { setItems, setOnlyItem, enableItems, onAddSeries }, seriesSelection)

  // A built-in with no data isn't drawn, so offering it in + Add series would make picking it do nothing.
  const popoverShownMetricIds = useMemo(() => {
    if (!selection.plottedMetricIds) return alreadyShownMetricIds
    const listedIds = new Set(filteredItems.map(item => item.id))
    const noDataIds = items.filter(item => !listedIds.has(item.id)).map(item => item.id)
    return new Set([...selection.plottedMetricIds, ...noDataIds])
  }, [selection.plottedMetricIds, alreadyShownMetricIds, filteredItems, items])
  // An unplotted, uncolored item would fail MultiLine's and AnalyzeTooltip's required color prop.
  const chartItems = useMemo(() => filteredItems.filter(item => !item.disabled || item.color), [filteredItems])

  const {
    toggle,
    toggleOnly,
    focusChartLine,
    blurChartLine,
    handleSetDate,
    isOnlyMode,
    onlyLegendText,
    focusedChartLine,
  } = useMultiLineHandlers(filteredItems, selection.setItems, selection.setOnlyItem, selection.enableItems, null)

  // Distinguish "no rows returned" from "rows exist but nothing selected has a value" for messaging.
  if (!data || data.length === 0) {
    return <GraphEmptyState message={noDataMessage ?? emptyMessage} />
  }

  if (!filteredItems || filteredItems.length === 0) {
    return <GraphEmptyState message={emptyMessage} />
  }

  const chart = (
    <MultiLine
      data={data}
      resizableRef={graphRef}
      items={chartItems}
      startDate={startDate}
      endDate={endDate}
      setDate={handleSetDate}
      toggle={toggle}
      toggleOnly={toggleOnly}
      focusChartLine={focusChartLine}
      blurChartLine={blurChartLine}
      focusedChartLine={focusedChartLine}
      onlyItem={isOnlyMode}
      tickFormat={formatter}
      numYTicks={numYTicks}
      thresholdParams={thresholdParams}
      width={width}
      height={height}
      onDotClick={onDotClick}
      isOneDayMode={isOneDayMode}
      tooltip={
        <AnalyzeTooltip
          data={data}
          items={chartItems}
          focusedChartLine={focusedChartLine}
          formatter={formatter}
          intradayChartMode={intradayChartMode}
          hasDotClick={Boolean(onDotClick)}
        />
      }
    />
  )

  return (<div className="graph-content">
    <ChartNotice isTruncated={isTruncated} hasFailedBatches={hasFailedBatches} />
    {data && (
      <div className={`enhanced-multi-line-container legend-placement-${legendPlacement}`}>
        {legendPlacement !== "right" && (
          <div className="graph-top">
            <ScrollableLegend className="legend">
              {filteredItems.map(item =>
                <LegendItem
                  key={item.id}
                  {...item}
                  onlyLegendText={onlyLegendText}
                  onClick={toggle}
                  onOnlyToggle={toggleOnly}
                  focus={focusChartLine}
                  blur={blurChartLine}
                  focused={focusedChartLine}
                />,
              )}
            </ScrollableLegend>
          </div>
        )}
        {legendPlacement === "right"
          ? (
            <div className="graph-body">
              <div className="graph-chart-area">{chart}</div>
              <LegendPanel
                items={filteredItems}
                visibleCount={visibleCount}
                latestValues={latestValues}
                latestDates={latestDates}
                formatter={formatter}
                onlyLegendText={onlyLegendText}
                onClick={toggle}
                onOnlyToggle={toggleOnly}
                onValueClick={onLegendValueClick && handleLegendValueClick}
                focus={focusChartLine}
                blur={blurChartLine}
                focused={focusedChartLine}
                addSeriesMetrics={addSeriesMetrics}
                alreadyShownMetricIds={popoverShownMetricIds}
                onAddSeries={selection.onAddSeries}
                noDataAddedCount={noDataAddedCount}
              />
            </div>
          )
          : chart}
      </div>
    )}
  </div>)
}

MultiLineGraphComponent.propTypes = {
  data: multiDateContract.isRequired,
  endDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(MULTI_LINE_ITEM_SHAPE).isRequired,
  filterItems: PropTypes.func,
  setItems: PropTypes.func,
  setOnlyItem: PropTypes.func,
  enableItems: PropTypes.func,
  emptyMessage: PropTypes.string,
  noDataMessage: PropTypes.string,
  formatter: PropTypes.func,
  thresholdParams: THRESHOLD_PARAMS,
  onDotClick: PropTypes.func,
  onLegendValueClick: PropTypes.func,
  isOneDayMode: PropTypes.bool,
  intradayChartMode: PropTypes.oneOf(["none", "liveTick", "batchTrend"]),
  isTruncated: PropTypes.bool,
  hasFailedBatches: PropTypes.bool,
  // Only "right" needs to render correctly yet; "inline" just preserves existing behavior.
  legendPlacement: PropTypes.oneOf(["right", "inline"]),
  addSeriesMetrics: ADD_SERIES_METRICS_SHAPE,
  alreadyShownMetricIds: PropTypes.instanceOf(Set),
  onAddSeries: PropTypes.func,
  seriesSelection: SERIES_SELECTION_SHAPE,
  addedMetricIds: PropTypes.arrayOf(PropTypes.string),
}

export default function MultiLineGraph({ status, loadingMessage = "", ...props }) {
  if (status === LOADING) {
    return <GraphLoadingState message={loadingMessage} />
  }

  return (
    <ErrorBoundary fallback={GraphErrorFallback}>
      <MultiLineGraphWithError {...props} />
    </ErrorBoundary>
  )
}

MultiLineGraph.propTypes = {
  status: PropTypes.oneOf([LOADING, ERROR, OK]),
  data: multiDateContract,
  endDate: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(MULTI_LINE_ITEM_SHAPE).isRequired,
  filterItems: PropTypes.func,
  setItems: PropTypes.func,
  setOnlyItem: PropTypes.func,
  enableItems: PropTypes.func,
  loadingMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
  noDataMessage: PropTypes.string,
  formatter: PropTypes.func,
  thresholdParams: THRESHOLD_PARAMS,
  onDotClick: PropTypes.func,
  onLegendValueClick: PropTypes.func,
  isOneDayMode: PropTypes.bool,
  intradayChartMode: PropTypes.oneOf(["none", "liveTick", "batchTrend"]),
  isTruncated: PropTypes.bool,
  hasFailedBatches: PropTypes.bool,
  legendPlacement: PropTypes.oneOf(["right", "inline"]),
  addSeriesMetrics: ADD_SERIES_METRICS_SHAPE,
  alreadyShownMetricIds: PropTypes.instanceOf(Set),
  onAddSeries: PropTypes.func,
  seriesSelection: SERIES_SELECTION_SHAPE,
  addedMetricIds: PropTypes.arrayOf(PropTypes.string),
}
